import { type ReactNode, useEffect, useRef } from 'react';
import { Box, IconButton, Paper, Popover, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface ContextHintProps {
  hintId: string;
  title: string;
  body: string;
  visible: boolean;
  onDismiss: (hintId: string) => void;
  onDismissAll: () => void;
  onSeen: (hintId: string) => void;
  placement?: 'bottom' | 'top' | 'left' | 'right';
  children: ReactNode;
}

export function ContextHint({
  hintId,
  title,
  body,
  visible,
  onDismiss,
  onDismissAll,
  onSeen,
  placement = 'bottom',
  children,
}: ContextHintProps) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const seenRef = useRef(false);

  // Trigger markSeen on mount
  useEffect(() => {
    if (!seenRef.current) {
      seenRef.current = true;
      onSeen(hintId);
    }
  }, [hintId, onSeen]);

  const anchorOrigin = getAnchorOrigin(placement);
  const transformOrigin = getTransformOrigin(placement);

  return (
    <Box ref={anchorRef} sx={{ display: 'inline' }}>
      {children}
      <Popover
        open={visible}
        anchorEl={anchorRef.current}
        onClose={() => onDismiss(hintId)}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(18, 24, 38, 0.98)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(45, 212, 191, 0.2)',
              borderRadius: 2,
              maxWidth: 300,
              p: 0,
            },
          },
        }}
      >
        <Paper elevation={0} sx={{ bgcolor: 'transparent', p: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={600} color="primary.light">
                {title}
              </Typography>
              <IconButton size="small" onClick={() => onDismiss(hintId)} sx={{ ml: 1, mt: -0.5 }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
              {body}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                cursor: 'pointer',
                '&:hover': { color: 'text.secondary' },
                fontSize: 11,
              }}
              onClick={() => onDismissAll()}
            >
              {t('onboarding.hints.dismissAll')}
            </Typography>
          </Stack>
        </Paper>
      </Popover>
    </Box>
  );
}

function getAnchorOrigin(placement: string) {
  switch (placement) {
    case 'top':
      return { vertical: 'top' as const, horizontal: 'center' as const };
    case 'left':
      return { vertical: 'center' as const, horizontal: 'left' as const };
    case 'right':
      return { vertical: 'center' as const, horizontal: 'right' as const };
    default: // bottom
      return { vertical: 'bottom' as const, horizontal: 'center' as const };
  }
}

function getTransformOrigin(placement: string) {
  switch (placement) {
    case 'top':
      return { vertical: 'bottom' as const, horizontal: 'center' as const };
    case 'left':
      return { vertical: 'center' as const, horizontal: 'right' as const };
    case 'right':
      return { vertical: 'center' as const, horizontal: 'left' as const };
    default: // bottom
      return { vertical: 'top' as const, horizontal: 'center' as const };
  }
}
